import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const PaymentMethodForm = ({ formData, errors, onChange, selectedPlan, paymentReference, gymId }) => {
  const [paymentMethod, setPaymentMethod] = useState(formData?.paymentMethod || 'card');
  const [gymZelleEmail, setGymZelleEmail] = useState(null);
  const [loadingGymInfo, setLoadingGymInfo] = useState(false);

  useEffect(() => {
    if (gymId) {
      loadGymZelleInfo();
    }
  }, [gymId]);

  const loadGymZelleInfo = async () => {
    setLoadingGymInfo(true);
    try {
      const { data, error } = await supabase?.from('gyms')?.select('zelle_email, zelle_payment_enabled')?.eq('id', gymId)?.single();
      
      if (!error && data?.zelle_payment_enabled && data?.zelle_email) {
        setGymZelleEmail(data?.zelle_email);
      }
    } catch (err) {
      console.error('Error loading gym Zelle info:', err);
    } finally {
      setLoadingGymInfo(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    onChange({ ...formData, [name]: value });
  };

  const handleMethodChange = (method) => {
    setPaymentMethod(method);
    onChange({ ...formData, paymentMethod: method });
  };

  const paymentMethods = [
    { id: 'card', label: 'Credit/Debit Card', icon: 'CreditCard' },
    { id: 'paypal', label: 'PayPal', icon: 'Wallet' },
    { id: 'bank', label: 'Bank Transfer', icon: 'Building2' },
    ...(gymZelleEmail ? [{ id: 'zelle', label: 'Zelle', icon: 'Smartphone' }] : []),
    { id: 'pay_at_gym', label: 'Pay at Gym', icon: 'Store' }
  ];

  return (
    <div className="bg-card rounded-xl p-6 md:p-8 shadow-md border border-border">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="DollarSign" size={20} color="var(--color-primary)" />
        </div>
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
          Payment Information
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 md:mb-8">
        {paymentMethods?.map((method) => (
          <button
            key={method?.id}
            onClick={() => handleMethodChange(method?.id)}
            className={`
              flex flex-col items-center gap-3 p-4 md:p-6 rounded-lg border-2 transition-all duration-250
              ${paymentMethod === method?.id
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }
            `}
          >
            <Icon
              name={method?.icon}
              size={32}
              color={paymentMethod === method?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
            />
            <span
              className={`text-sm font-medium ${
                paymentMethod === method?.id ? 'text-primary' : 'text-foreground'
              }`}
            >
              {method?.label}
            </span>
          </button>
        ))}
      </div>
      {paymentMethod === 'card' && (
        <div className="space-y-4 md:space-y-6">
          <Input
            label="Cardholder Name"
            type="text"
            name="cardholderName"
            placeholder="Name on card"
            value={formData?.cardholderName}
            onChange={handleChange}
            error={errors?.cardholderName}
            required
          />

          <Input
            label="Card Number"
            type="text"
            name="cardNumber"
            placeholder="1234 5678 9012 3456"
            value={formData?.cardNumber}
            onChange={handleChange}
            error={errors?.cardNumber}
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Expiry Date"
              type="text"
              name="expiryDate"
              placeholder="MM/YY"
              value={formData?.expiryDate}
              onChange={handleChange}
              error={errors?.expiryDate}
              required
            />

            <Input
              label="CVV"
              type="text"
              name="cvv"
              placeholder="123"
              value={formData?.cvv}
              onChange={handleChange}
              error={errors?.cvv}
              required
            />
          </div>

          <div className="bg-muted rounded-lg p-4 flex items-start gap-3">
            <Icon name="Lock" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Secure Payment</p>
              <p className="text-xs text-muted-foreground mt-1">
                Your payment information is encrypted and secure. We use industry-standard SSL encryption.
              </p>
            </div>
          </div>
        </div>
      )}
      {paymentMethod === 'paypal' && (
        <div className="text-center py-8 md:py-12">
          <Icon name="Wallet" size={64} color="var(--color-primary)" className="mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            PayPal Payment
          </h3>
          <p className="text-sm text-muted-foreground mb-6">
            You'll be redirected to PayPal to complete your payment securely
          </p>
          <Button variant="outline" iconName="ExternalLink" iconPosition="right">
            Continue with PayPal
          </Button>
        </div>
      )}
      {paymentMethod === 'bank' && (
        <div className="space-y-4 md:space-y-6">
          <Input
            label="Account Holder Name"
            type="text"
            name="accountHolderName"
            placeholder="Full name"
            value={formData?.accountHolderName}
            onChange={handleChange}
            required
          />

          <Input
            label="Account Number"
            type="text"
            name="accountNumber"
            placeholder="Enter account number"
            value={formData?.accountNumber}
            onChange={handleChange}
            required
          />

          <Input
            label="Routing Number"
            type="text"
            name="routingNumber"
            placeholder="9-digit routing number"
            value={formData?.routingNumber}
            onChange={handleChange}
            required
          />

          <div className="bg-warning/10 rounded-lg p-4 flex items-start gap-3">
            <Icon name="AlertCircle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Bank Transfer Processing</p>
              <p className="text-xs text-muted-foreground mt-1">
                Bank transfers may take 3-5 business days to process. Your membership will be activated once payment is confirmed.
              </p>
            </div>
          </div>
        </div>
      )}
      {paymentMethod === 'pay_at_gym' && (
        <div className="space-y-6">
          <div className="bg-primary/10 rounded-lg p-6 border border-primary/20">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Icon name="Store" size={24} color="var(--color-primary)" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Pay at Gym Location
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Complete your registration now and pay when you visit the gym. Your account will be activated once payment is received.
                </p>
                {selectedPlan && (
                  <div className="bg-card rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">Amount Due:</span>
                      <span className="text-2xl font-bold text-primary">
                        ${selectedPlan?.price}
                        <span className="text-sm text-muted-foreground">/{selectedPlan?.billingCycle}</span>
                      </span>
                    </div>
                  </div>
                )}
                {paymentReference && (
                  <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <Icon name="CheckCircle2" size={20} color="var(--color-success)" />
                      <span className="font-semibold text-foreground">Payment Reference Generated</span>
                    </div>
                    <div className="bg-card rounded-lg p-4">
                      <p className="text-xs text-muted-foreground mb-2">Your Payment Reference Number:</p>
                      <p className="text-2xl font-mono font-bold text-foreground tracking-wider">
                        {paymentReference?.reference_number}
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3">
                      Please provide this reference number when making payment at the gym.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-warning/10 rounded-lg p-4 flex items-start gap-3">
            <Icon name="AlertCircle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Important Information</p>
              <ul className="text-xs text-muted-foreground mt-2 space-y-1 list-disc list-inside">
                <li>Your account will remain pending until payment is received</li>
                <li>Visit your selected gym location to complete payment</li>
                <li>Bring your payment reference number for quick processing</li>
                <li>Accepted payment methods at gym: Cash, Card, Check</li>
                <li>Your membership will be activated immediately upon payment</li>
              </ul>
            </div>
          </div>
        </div>
      )}
      {paymentMethod === 'zelle' && gymZelleEmail && (
        <div className="space-y-6">
          <div className="bg-warning/5 border border-warning/20 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-warning/20 flex items-center justify-center flex-shrink-0">
                <Icon name="Smartphone" size={24} color="var(--color-warning)" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Zelle Payment Instructions
                </h3>
                <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside mb-4">
                  <li>Send payment to: <strong className="text-foreground">{gymZelleEmail}</strong></li>
                  <li>Amount: <strong className="text-foreground">${selectedPlan?.price}</strong></li>
                  <li>Include your full name in the payment note</li>
                  <li>Enter your Zelle details and confirmation number below</li>
                </ol>
                {selectedPlan && (
                  <div className="bg-card rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">Amount Due:</span>
                      <span className="text-2xl font-bold text-primary">
                        ${selectedPlan?.price}
                        <span className="text-sm text-muted-foreground">/{selectedPlan?.billingCycle}</span>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <Input
              label="Zelle Email or Phone (used for payment)"
              type="text"
              name="zelleContact"
              placeholder="your-email@example.com or (555) 123-4567"
              value={formData?.zelleContact}
              onChange={handleChange}
              error={errors?.zelleContact}
              required
            />

            <Input
              label="Zelle Confirmation Number"
              type="text"
              name="zelleConfirmationNumber"
              placeholder="Enter confirmation number from Zelle"
              value={formData?.zelleConfirmationNumber}
              onChange={handleChange}
              error={errors?.zelleConfirmationNumber}
              required
            />
          </div>

          <div className="bg-info/10 rounded-lg p-4 flex items-start gap-3">
            <Icon name="Info" size={20} color="var(--color-info)" className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Manual Approval Required</p>
              <ul className="text-xs text-muted-foreground mt-2 space-y-1 list-disc list-inside">
                <li>Your payment will be verified by gym staff (typically within 24-48 hours)</li>
                <li>You'll receive a confirmation email once your payment is approved</li>
                <li>Your membership will be activated after approval</li>
                <li>Keep your confirmation number for reference</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMethodForm;